import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ContentCard from '@components/course-landing-page/content-card'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { Investors } from '@components/book-trial-class-jp/investors'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

export const MitAppInventorSeo = formatSeoConfig({
  title: 'MIT App Inventor for Kids - Build Mobile Apps Easily',
  description:
    'Learn app development with MIT App Inventor for kids. Step-by-step coding lessons to build your own mobile apps. Enroll now and create!',
  url: 'https://www.codingal.com/courses/mit-app-inventor-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/mit-app-inventor.jpg',
})

const { MIT_APP_INVENTOR } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['ad'].trialCourseQuery}`

const MITAppInventor = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...MitAppInventorSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[MIT_APP_INVENTOR].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn MIT App Inventor with the Top Coding Course Instructors for Kids and Teens"
        description="Want your child to go beyond just using apps? With MIT App Inventor, they’ll learn how to build them. Codingal’s MIT App Inventor course for kids and teens introduces the fundamentals of mobile app development using a drag-and-drop coding environment, powered by real programming logic. It’s the perfect platform for beginners—and with our expert instructors, your child will create real apps while gaining essential AI & coding skills that will prepare them for the digital future."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={MIT_APP_INVENTOR}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <ClassCurriculum
            courseName={MIT_APP_INVENTOR}
            title="Our MIT App Inventor class curriculum"
            description={
              <Trans>
                <p>
                  The curriculum is structured to make app development simple,
                  engaging, and scalable for kids. They’ll start with basic
                  app-building concepts, progress to creative project work, and
                  gradually move toward developing apps that integrate
                  AI-powered elements like text-to-speech or translation tools.
                </p>
                <p>
                  By the end of the course, your child will be confidently
                  designing apps and thinking like a developer—with a blend of
                  creativity and logic that sets them up for advanced coding and
                  AI learning.
                </p>
              </Trans>
            }
          />
          <Activities courseName={MIT_APP_INVENTOR} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked coding tutors
                ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                All Codingal instructors are trained, background-verified, and
                skilled in teaching kids. They not only guide students through
                app development, but also foster a love for technology by making
                each session interactive and personalized. With a blend of
                structured lessons and creative freedom, our teachers make sure
                kids stay curious, engaged, and inspired to build with code—and
                now, with AI tools too.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={MIT_APP_INVENTOR}
            title={<Trans>An engaging MIT App Inventor class for kids</Trans>}
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  MIT App Inventor is an ideal starting point for beginners, and
                  Codingal enhances the learning experience with a curriculum
                  aligned with the K–12 Computer Science Framework. Our course
                  not only covers essential tech principles but also gives
                  students the freedom to explore and unleash their creativity.
                </span>
                <span className="block">
                  Whether they&lsquo;re first-time coders or budding tech
                  enthusiasts, every student will build a strong foundation in
                  coding, logic, and AI-powered tools, preparing them for future
                  success in the digital world.
                </span>
              </Trans>
            }
            courseName={MIT_APP_INVENTOR}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                Upon course completion, students receive the Codingal Developer
                Certificate, a STEM.org-accredited credential. It serves as a
                valuable early milestone in their AI & Coding journey and can be
                showcased on portfolios, resumes, and school applications.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your MIT App Inventor developer certification"
            description={
              <>
                <p>
                  Completing the course isn’t just fun—it’s a significant
                  achievement. Every student earns a Codingal Developer
                  Certificate upon finishing the MIT App Inventor course,
                  validating their skills in app design and development.
                </p>
                <p>
                  Upon completion, students receive a certificate they can
                  proudly showcase—an early credential that marks a milestone in
                  their tech journey and sets them up for future success.
                </p>
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
            urlQuery={urlQuery}
          />
          <CodingForStudents
            title="Our MIT App Inventor course is appropriate for Elementary school students"
            description={
              <>
                This course is tailored to engage learners across all age
                groups. Younger students in elementary school start with
                foundational concepts through fun, interactive app projects.
                Middle and high school students can build on these skills and
                explore more advanced features like variables, conditionals, and
                AI extensions—perfect for students beginning their coding
                journey. With expert support and a structured curriculum,
                students can learn at their own pace, gaining the confidence and
                skills to design their own apps. For high schoolers, while MIT
                App Inventor offers a solid introduction to app development,
                more advanced programming tools and languages may be needed for
                complex projects.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <Faq
            courseName={MIT_APP_INVENTOR}
            title="Frequently Asked Questions"
          />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses
            description={
              <>
                Once your child has explored the basics of app development with
                MIT App Inventor, there’s a whole world of coding and creativity
                waiting to be unlocked! We recommend pairing this course with
                other beginner-friendly and exciting programs to keep the
                momentum going. With MIT App Inventor, your child will not only
                learn to create functional mobile apps but also gain the
                foundational skills to dive deeper into more advanced coding and
                app development in the future.
              </>
            }
            courseCodes={['wd', 'aiepc', 'pc', 'ds', 'ioi']}
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={MIT_APP_INVENTOR}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="MIT App Inventor for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default MITAppInventor
